import type {
  BoCatalog,
  BoOption,
  BoSection,
  ConfigDocument,
  ManifestDocument,
  ManifestSection,
} from './types'

/**
 * A version rebuilt as a catalogue, so the SAME preview studio the editors use can try it on.
 *
 * Two sources on purpose: the manifest is the truth for structure and tier rules but carries
 * object KEYS, and only the config document knows the asset origin — so structure comes from one
 * and layer URLs from the other, joined by slug.
 */
export function catalogueFromVersion(
  manifest: ManifestDocument,
  config: ConfigDocument,
  setId: string,
): BoCatalog | null {
  const set = manifest.sets.find((s) => s.id === setId)
  if (!set) {
    return null
  }
  const urls = new Map(
    config.categories.flatMap((category) =>
      (category.options ?? []).map(
        (o) => [`${category.id}/${o.slug}`, o.layers ?? {}] as const,
      ),
    ),
  )

  const sections: BoSection[] = []
  const options: BoOption[] = []
  set.categories.forEach((def) => {
    sections.push(section(def, 'option'))
    const active = (def.options ?? []).filter((o) => o.status !== 'retired')
    active.forEach((o, index) =>
      options.push({
        id: `${def.id}/${o.slug}`,
        category: def.id,
        slug: o.slug,
        displayName: o.displayName ?? o.slug,
        sortOrder: index,
        status: 'active',
        visibility: {},
        createdAt: '',
        layers: urls.get(`${def.id}/${o.slug}`) ?? {},
      }),
    )
  })
  const colours = set.colorCategories ?? []
  colours.forEach((def) => sections.push(section(def, 'color')))
  // The order the catalogue API serves and the rail shows: `paint_order nulls last, id`. The
  // manifest keeps pictures and colours in separate lists, which read as "pictures first" here.
  sections.sort(
    (a, b) =>
      (a.paintOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.paintOrder ?? Number.MAX_SAFE_INTEGER) || a.id.localeCompare(b.id),
  )

  return {
    set: {
      id: setId,
      name: config.displayName ?? setId,
      description: '',
      badge: null,
      required: [...(config.required ?? [])],
      defaults: { ...(config.defaults ?? {}) },
      createdAt: '',
    },
    sections,
    options,
  }
}

function section(def: ManifestSection, kind: 'option' | 'color'): BoSection {
  const active = (def.options ?? []).filter((o) => o.status !== 'retired')

  return {
    id: def.id,
    kind,
    displayName: def.displayName ?? def.id,
    displayIcon: null,
    paintOrder: def.paintOrder ?? null,
    backPaintOrder: def.backPaintOrder ?? null,
    visibility: def.visibility ?? {},
    lockedDisplay: def.lockedDisplay ?? null,
    swatches: def.swatches ? [...def.swatches] : null,
    tints: { ...(def.tints ?? {}) },
    renderOpacity: def.renderOpacity ?? 1,
    renderFit: def.renderFit ?? 'fill',
    tierRule: def.tierRule ?? null,
    createdAt: null,
    optionCount: active.length,
    retiredOptionCount: (def.options ?? []).length - active.length,
    retiredSwatches: [...(def.retiredSwatches ?? [])],
    unregistered: false,
  }
}
